"use client";

import React, { createContext, useContext, useEffect, useCallback, ReactNode, useMemo } from 'react';
import { useGame } from './GameContextProvider';
import { GameMode, GameModeStatus, GameHazard } from '@/lib/types/common/GameMode';
import { GameModeAdvancedSettings, DEFAULT_GAME_MODE_SETTINGS, FightModeSettings, CoopModeSettings, FlashModeSettings, HazardModeSettings } from '@/lib/types/common/GameModeAdvancedSettings';
import useGameModeSettings from '@/hooks/useGameModeSettings';

interface GameModeSettingsContextValue {
    settings: GameModeAdvancedSettings;
    updateSetting: <K extends keyof GameModeAdvancedSettings>(key: K, value: GameModeAdvancedSettings[K]) => void;
    updateSettings: (newSettings: Partial<GameModeAdvancedSettings>) => void;
    isGameMaster: boolean;
    gameMode: GameMode;
}

const GameModeSettingsContext = createContext<GameModeSettingsContextValue | null>(null);

interface GameModeSettingsProviderProps {
    children: ReactNode;
    gameMode: GameMode;
    onSettingsChange: (settings: GameModeAdvancedSettings) => void;
}

export function GameModeSettingsProvider({ children, gameMode, onSettingsChange }: GameModeSettingsProviderProps) {
    const { room, gameState } = useGame();
    const { settings: savedSettings, saveSettings } = useGameModeSettings(gameMode);

    const isGameMaster = room?.sessionId === gameState?.gameMasterId;

    // Get server state settings
    const serverSettings = gameState?.gameSettings;

    // Get all hazards as default enabled (for hazard mode)
    const allHazards = Object.values(GameHazard);

    // Get enabled hazards from game state (convert ArraySchema to array if needed)
    const getEnabledHazardsFromGameState = (): string[] | undefined => {
        if (!gameState?.gameSettings || (gameState.gameSettings.gameMode !== GameMode.HAZARD && gameState.gameSettings.gameMode !== GameMode.CUSTOM)) return undefined;
        const enabledHazards = gameState.gameSettings.enabledHazards;
        if (!enabledHazards || enabledHazards.length === 0) return undefined;
        return Array.from(enabledHazards);
    };

    // Build the settings based on game mode and whether user is game master
    const settings = useMemo((): GameModeAdvancedSettings => {
        const baseDefaults = DEFAULT_GAME_MODE_SETTINGS[gameMode] ?? DEFAULT_GAME_MODE_SETTINGS[GameMode.CLASSIC];

        if (isGameMaster) {
            // Game master uses saved settings
            switch (gameMode) {
                case GameMode.FIGHT:
                    return {
                        gameMode: GameMode.FIGHT,
                        maxRounds: -1,
                        timerSeconds: savedSettings?.timerSeconds ?? 60,
                        hasSpecialRules: true,
                        status: GameModeStatus.AVAILABLE,
                        minPlayers: 2,
                        maxPlayers: 8,
                        teamMaxHp: (savedSettings as FightModeSettings)?.teamMaxHp ?? 10000,
                        numberOfTeams: (savedSettings as FightModeSettings)?.numberOfTeams ?? 2,
                        damageMultiplierScaling: (savedSettings as FightModeSettings)?.damageMultiplierScaling ?? 'normal'
                    };
                case GameMode.COOP:
                    return {
                        gameMode: GameMode.COOP,
                        maxRounds: savedSettings?.maxRounds ?? 5,
                        timerSeconds: savedSettings?.timerSeconds ?? 60,
                        hasSpecialRules: true,
                        status: GameModeStatus.AVAILABLE,
                        minPlayers: 2,
                        maxPlayers: 8,
                        numberOfTeams: (savedSettings as CoopModeSettings)?.numberOfTeams ?? 1
                    };
                case GameMode.FLASH:
                    return {
                        gameMode: GameMode.FLASH,
                        maxRounds: savedSettings?.maxRounds ?? 5,
                        timerSeconds: null,
                        flashDuration: (savedSettings as FlashModeSettings)?.flashDuration ?? 1000,
                        hasSpecialRules: savedSettings?.hasSpecialRules ?? false,
                        status: savedSettings?.status ?? GameModeStatus.AVAILABLE,
                        minPlayers: savedSettings?.minPlayers ?? 1,
                        maxPlayers: savedSettings?.maxPlayers ?? 8
                    };
                case GameMode.HAZARD:
                    return {
                        gameMode: GameMode.HAZARD,
                        maxRounds: savedSettings?.maxRounds ?? 5,
                        timerSeconds: savedSettings?.timerSeconds ?? 60,
                        hasSpecialRules: true,
                        status: GameModeStatus.AVAILABLE,
                        minPlayers: 1,
                        maxPlayers: 8,
                        hazardsPerRound: (savedSettings as HazardModeSettings)?.hazardsPerRound ?? 1,
                        enabledHazards: (savedSettings as HazardModeSettings)?.enabledHazards ?? allHazards
                    };
                default:
                    return {
                        ...baseDefaults,
                        ...savedSettings
                    };
            }
        } else {
            // Non-game-master uses server state
            switch (gameMode) {
                case GameMode.FIGHT:
                    return {
                        gameMode: GameMode.FIGHT,
                        maxRounds: -1,
                        timerSeconds: (serverSettings?.timerSeconds && serverSettings.timerSeconds > 0 ? serverSettings.timerSeconds : 60),
                        hasSpecialRules: true,
                        status: serverSettings?.status ?? GameModeStatus.AVAILABLE,
                        minPlayers: serverSettings?.minPlayers ?? 2,
                        maxPlayers: serverSettings?.maxPlayers ?? 8,
                        teamMaxHp: serverSettings?.teamMaxHp ?? 10000,
                        numberOfTeams: serverSettings?.numberOfTeams ?? 2,
                        damageMultiplierScaling: serverSettings?.damageMultiplierScaling ?? 'normal'
                    };
                case GameMode.COOP:
                    return {
                        gameMode: GameMode.COOP,
                        maxRounds: serverSettings?.maxRounds ?? 5,
                        timerSeconds: (serverSettings?.timerSeconds && serverSettings.timerSeconds > 0 ? serverSettings.timerSeconds : 60),
                        hasSpecialRules: true,
                        status: serverSettings?.status ?? GameModeStatus.AVAILABLE,
                        minPlayers: 2,
                        maxPlayers: 8,
                        numberOfTeams: serverSettings?.numberOfTeams ?? 1
                    };
                case GameMode.FLASH:
                    return {
                        gameMode: GameMode.FLASH,
                        maxRounds: serverSettings?.maxRounds ?? 5,
                        timerSeconds: null,
                        flashDuration: serverSettings?.flashDuration ?? 1000,
                        hasSpecialRules: serverSettings?.hasSpecialRules ?? false,
                        status: serverSettings?.status ?? GameModeStatus.AVAILABLE,
                        minPlayers: serverSettings?.minPlayers ?? 1,
                        maxPlayers: serverSettings?.maxPlayers ?? 8
                    };
                case GameMode.HAZARD:
                    return {
                        gameMode: GameMode.HAZARD,
                        maxRounds: serverSettings?.maxRounds ?? 5,
                        timerSeconds: (serverSettings?.timerSeconds && serverSettings.timerSeconds > 0 ? serverSettings.timerSeconds : 60),
                        hasSpecialRules: true,
                        status: serverSettings?.status ?? GameModeStatus.AVAILABLE,
                        minPlayers: serverSettings?.minPlayers ?? 1,
                        maxPlayers: serverSettings?.maxPlayers ?? 8,
                        hazardsPerRound: serverSettings?.hazardsPerRound ?? 1,
                        enabledHazards: getEnabledHazardsFromGameState() ?? allHazards
                    };
                default:
                    return {
                        ...baseDefaults,
                        maxRounds: serverSettings?.maxRounds ?? baseDefaults.maxRounds,
                        timerSeconds: serverSettings?.timerSeconds ?? baseDefaults.timerSeconds,
                        status: serverSettings?.status ?? baseDefaults.status
                    } as GameModeAdvancedSettings;
            }
        }
    }, [gameMode, isGameMaster, savedSettings, serverSettings, allHazards]);

    // Send initial settings when component mounts (for game master)
    useEffect(() => {
        if (isGameMaster) {
            onSettingsChange(settings);
        }
    }, []);

    const updateSettings = useCallback((newPartialSettings: Partial<GameModeAdvancedSettings>) => {
        const newSettings = {
            ...settings,
            ...newPartialSettings
        } as GameModeAdvancedSettings;

        // Save to localStorage
        saveSettings(newSettings);

        // Send to server
        onSettingsChange(newSettings);
    }, [settings, saveSettings, onSettingsChange]);

    const updateSetting = useCallback(<K extends keyof GameModeAdvancedSettings>(
        key: K,
        value: GameModeAdvancedSettings[K]
    ) => {
        updateSettings({ [key]: value } as Partial<GameModeAdvancedSettings>);
    }, [updateSettings]);

    const contextValue = useMemo(() => ({
        settings,
        updateSetting,
        updateSettings,
        isGameMaster,
        gameMode
    }), [settings, updateSetting, updateSettings, isGameMaster, gameMode]);

    return (
        <GameModeSettingsContext.Provider value={contextValue}>
            {children}
        </GameModeSettingsContext.Provider>
    );
}

export function useGameModeSettingsContext() {
    const context = useContext(GameModeSettingsContext);
    if (!context) {
        throw new Error('useGameModeSettingsContext must be used within a GameModeSettingsProvider');
    }
    return context;
}

export default GameModeSettingsProvider;
